import { graphqlPost } from "api/graphqlClient";
import NotificationService from "components/Notification/Notification.service";
import { singleQuery } from "api/graphqlTypes";
import { actions as authActions } from "containers/Auth/Auth.reducer";
import { actions as workspacesActions } from "containers/Workspaces/Workspaces.reducer";
import { call, put, takeEvery, takeLatest } from "redux-saga/effects";

/**
 * Fetch Workspace
 * @param  {Object} promise
 * @param {string} id Workspace ID
 * @return {Object} response from API
 */
export function* fetchSingleWorkspace({ promise, id, params, silent = false }) {
    try {
        const fetchSingleWorkspaceAPI = yield graphqlPost(params);
        yield put({
            type: workspacesActions.FETCH_WORKSPACE_SUCCESSFUL,
        });
        yield put({
            type: workspacesActions.STORE_WORKSPACE_DATA,
            iri: id,
            response: fetchSingleWorkspaceAPI?.data?.workspace,
        });
        yield call(promise.resolve);
    } catch (error) {
        yield put({
            type: workspacesActions.FETCH_WORKSPACE_FAILED,
        });
        if (!silent) {
            NotificationService.error(error?.response?.data || error); // TODO (medium) should use <RestErrorNotification> here, but leads to unexpected webpack error
        }
        yield call(promise.reject, error?.response?.data || error);
    }
}

// Workspace ids already loaded (or being loaded) in this page session.
const loadedWorkspaceIds = new Set();

export const resetLoadedWorkspaceIds = () => loadedWorkspaceIds.clear();

/**
 * Load the signed-in user's workspace record once per page session, whenever the
 * user identity is stored: by the login screen, or by the websocket `auth`
 * message when the app opens with an existing session. Without this the record
 * was loaded only by the login screen, so a session that skipped it (a new
 * browser profile, cleared storage, a session started outside the login form)
 * had no workspace data, and screens scoped to the workspace (the Executions
 * screen filters by workspace id) showed nothing. Loading it on every page
 * session also refreshes a stale persisted copy.
 * @param {Object} action STORE_USER_DATA action
 */
export function* loadUserWorkspace({ response }) {
    const workspaceId = response?.workspace_id;
    if (!workspaceId || loadedWorkspaceIds.has(workspaceId)) {
        return;
    }
    loadedWorkspaceIds.add(workspaceId);
    yield put({
        type: workspacesActions.START_FETCHING_WORKSPACE,
        id: workspaceId,
        silent: true,
        promise: { resolve: () => {}, reject: () => loadedWorkspaceIds.delete(workspaceId) },
        params: {
            query: singleQuery("workspace"),
            variables: { id: workspaceId },
        },
    });
}

/**
 * Register action to watcher
 */
export default [
    takeLatest(workspacesActions.START_FETCHING_WORKSPACE, fetchSingleWorkspace),
    takeEvery(authActions.STORE_USER_DATA, loadUserWorkspace),
];
